import { useMemo, useState } from 'react';
import type { StockRecommendation, ActionType } from '../api';

interface Props {
    recommendations: StockRecommendation[];
    onSelectSymbol: (symbol: string) => void;
}

type Horizon = 'H21' | 'H45' | 'H60';

const HORIZONS: { key: Horizon; label: string }[] = [
    { key: 'H21', label: '21 Days' },
    { key: 'H45', label: '45 Days' },
    { key: 'H60', label: '60 Days' },
];

export const TacticalRecommendationsZone = ({ recommendations, onSelectSymbol }: Props) => {
    const [horizon, setHorizon] = useState<Horizon>('H21');

    const activeTableData = useMemo(() => {
        return [...recommendations].sort((a, b) => {
            const actA = a.recommended_action[horizon];
            const actB = b.recommended_action[horizon];
            
            const score = (act: ActionType) => act === 'BUY' ? 2 : act === 'SELL' ? 1 : 0;
            if (score(actA) !== score(actB)) return score(actB) - score(actA);
            
            return (b.probabilities[horizon] || 0) - (a.probabilities[horizon] || 0);
        });
    }, [recommendations, horizon]);

    const kpis = useMemo(() => {
        let buys = 0, sells = 0, holds = 0;
        let sumBuyER = 0;
        
        recommendations.forEach(r => {
            const act = r.recommended_action[horizon];
            if (act === 'BUY') { buys++; sumBuyER += r.expected_returns[horizon] || 0; }
            else if (act === 'SELL') sells++;
            else holds++;
        });

        const total = buys + sells + holds;
        const avgBuyEr = buys > 0 ? sumBuyER / buys : 0;
        return { buys, sells, holds, total, avgBuyEr };
    }, [recommendations, horizon]);

    if (!recommendations.length) return null;

    return (
        <div className="mt-12 mb-4 animate-fade-in">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                        Tactical Daily Signals
                        <span className="px-2 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 rounded uppercase tracking-widest border border-emerald-200 dark:border-emerald-800">Live Engine</span>
                    </h2>
                    <p className="muted text-sm mt-1">
                        Actionable multi-horizon ML recommendations with parameterised expected returns and triple-barrier price guidance.
                    </p>
                </div>

                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700 self-start shrink-0 shadow-inner">
                    {HORIZONS.map((h) => (
                        <button
                            key={h.key}
                            onClick={() => setHorizon(h.key)}
                            className={`px-5 py-1.5 text-xs font-bold rounded-md transition-all ${
                                horizon === h.key
                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            {h.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="surface-card flex flex-col justify-center">
                    <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Signal Distribution</h3>
                    <div className="flex h-3 rounded-full overflow-hidden mb-2 bg-slate-100 dark:bg-slate-800">
                        <div style={{ width: `${(kpis.buys / (kpis.total || 1)) * 100}%` }} className="bg-emerald-500 transition-all duration-500" />
                        <div style={{ width: `${(kpis.holds / (kpis.total || 1)) * 100}%` }} className="bg-orange-500 transition-all duration-500" />
                        <div style={{ width: `${(kpis.sells / (kpis.total || 1)) * 100}%` }} className="bg-rose-500 transition-all duration-500" />
                    </div>
                    <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                        <span className="text-emerald-600 dark:text-emerald-400">{kpis.buys} Buys</span>
                        <span className="text-orange-600 dark:text-orange-400">{kpis.holds} Holds</span>
                        <span className="text-rose-600 dark:text-rose-400">{kpis.sells} Sells</span>
                    </div>
                </div>

                <div className="surface-card !p-4 flex flex-col justify-center border-l-4 border-l-emerald-500">
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Actionable Universe</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{(((kpis.buys + kpis.sells) / (kpis.total || 1)) * 100).toFixed(1)}%</p>
                </div>
                
                <div className="surface-card !p-4 flex flex-col justify-center border-l-4 border-l-indigo-500">
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Avg Buy Expected Return</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                        {kpis.avgBuyEr > 0 ? '+' : ''}{(kpis.avgBuyEr * 100).toFixed(2)}%
                    </p>
                </div>
            </div>

            <div className="surface-card !p-0 overflow-hidden flex flex-col">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-700 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="px-5 py-4">Asset</th>
                                <th className="px-5 py-4 text-center">Action</th>
                                <th className="px-5 py-4 w-40 text-right">Probability</th>
                                <th className="px-5 py-4 text-right">Exp. Return</th>
                                <th className="px-5 py-4 text-right">Price Guidance Bands</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {activeTableData.map((rec) => {
                                const action = rec.recommended_action[horizon];
                                const prob = rec.probabilities[horizon] ?? 0;
                                const er = rec.expected_returns[horizon] ?? 0;
                                const guidance = rec.price_guidance[horizon];

                                return (
                                    <tr 
                                        key={rec.symbol} 
                                        onClick={() => onSelectSymbol(rec.symbol)}
                                        className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group ${action === 'HOLD' ? 'opacity-60 hover:opacity-100' : ''}`}
                                    >
                                        <td className="px-5 py-3">
                                            <div className="font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{rec.symbol}</div>
                                            <div className="text-[11px] text-slate-500 truncate max-w-[150px]">{rec.company_name || rec.sector || '—'}</div>
                                        </td>
                                        <td className="px-5 py-3 text-center">
                                            <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded border ${
                                                action === 'BUY' ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-800' :
                                                action === 'SELL' ? 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-400 dark:border-rose-800' :
                                                'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-400 dark:border-orange-800'
                                            }`}>
                                                {action}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <span className={`font-mono text-xs ${
                                                    action === 'HOLD' ? 'text-orange-700 dark:text-orange-300' : 'text-slate-700 dark:text-slate-300'
                                                }`}>{(prob * 100).toFixed(1)}%</span>
                                                <div className="w-16 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full rounded-full ${action === 'BUY' ? 'bg-emerald-500' : action === 'SELL' ? 'bg-rose-500' : 'bg-orange-500'}`}
                                                        style={{ width: `${Math.min(Math.max(prob * 100, 0), 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <span className={`font-mono text-xs font-semibold ${er > 0 ? 'text-emerald-600 dark:text-emerald-400' : er < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'}`}>
                                                {er > 0 ? '+' : ''}{(er * 100).toFixed(2)}%
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            {/* Bands now render for ALL actions, including HOLD */}
                                            {guidance && (guidance.buy_below !== null || guidance.sell_above !== null) ? (
                                                <div className="flex flex-col items-end gap-1 text-[11px] font-mono">
                                                    {action === 'BUY' && (
                                                        <>
                                                            {guidance.buy_below !== null && <span className="text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 rounded">Entry &lt; ${guidance.buy_below.toFixed(2)}</span>}
                                                            {guidance.sell_above !== null && <span className="text-slate-600 dark:text-slate-400">Target &gt; ${guidance.sell_above.toFixed(2)}</span>}
                                                            {guidance.stop !== null && <span className="text-slate-400">Stop: ${guidance.stop.toFixed(2)}</span>}
                                                        </>
                                                    )}

                                                    {/* In a long-only universe, SELL means rotate capital or trim profits */}
                                                    {action === 'SELL' && (
                                                        <>
                                                            {guidance.sell_above !== null && <span className="text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 px-1.5 rounded">Trim &gt; ${guidance.sell_above.toFixed(2)}</span>}
                                                            {guidance.buy_below !== null && <span className="text-slate-600 dark:text-slate-400">Re-enter &lt; ${guidance.buy_below.toFixed(2)}</span>}
                                                        </>
                                                    )}

                                                    {/* Unlocked HOLD support & resistance levels */}
                                                    {action === 'HOLD' && (
                                                        <>
                                                            {guidance.sell_above !== null && <span className="text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-1.5 rounded">Resist: ${guidance.sell_above.toFixed(2)}</span>}
                                                            {guidance.buy_below !== null && <span className="text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-1.5 rounded">Support: ${guidance.buy_below.toFixed(2)}</span>}
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400 text-xs italic">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
